import { post_hyperpayTransactions } from "@/api/hyperpay-transaction";

/** Mock / 接口未返回 requiredLevel 时的展示兜底，正式门槛以 eligibility.requiredLevel 为准 */
export const SPIKEX_CARD_UNLOCK_LEVEL = 6;

/** HyperPay 开卡/充值可选扣款币种 */
export const HYPERPAY_PAY_CURRENCIES = {
  USDT: "USDT",
  BRL: "BRL",
};

export const DEFAULT_HYPERPAY_PAY_CURRENCY = HYPERPAY_PAY_CURRENCIES.USDT;

/** 开卡/充值支付币种选项（对齐 Convert 币种选择器展示） */
export const SPIKEX_PAY_CURRENCY_OPTIONS = [
  {
    code: HYPERPAY_PAY_CURRENCIES.USDT,
    name: "Tether USDT",
    color: "#26A69A",
    isFiat: false,
  },
  {
    code: HYPERPAY_PAY_CURRENCIES.BRL,
    name: "Brazilian Real",
    color: "#128d7f",
    isFiat: true,
  },
];

export function resolveSpikexPayCurrencyOptions(cardInfo) {
  const codes = new Set(resolveHyperpayPayCurrencies(cardInfo));
  return SPIKEX_PAY_CURRENCY_OPTIONS.filter((option) => codes.has(option.code));
}

export function findSpikexPayCurrencyOption(code) {
  const normalized = normalizeHyperpayPayCurrency(code);
  return (
    SPIKEX_PAY_CURRENCY_OPTIONS.find((option) => option.code === normalized) || {
      code: normalized,
      name: normalized,
      color: "#26A69A",
      isFiat: normalized === HYPERPAY_PAY_CURRENCIES.BRL,
    }
  );
}

export function buildSpikexPayCurrencyBalanceMap(precheckByCurrency) {
  const map = {};
  if (!precheckByCurrency || typeof precheckByCurrency !== "object") return map;
  Object.entries(precheckByCurrency).forEach(([currency, precheck]) => {
    if (precheck?.available == null || precheck.available === "") return;
    map[normalizeHyperpayPayCurrency(currency)] = {
      amount: String(precheck.available),
    };
  });
  return map;
}

export function resolveSpikexPayCurrencyInsufficient(precheckByCurrency, currency) {
  const precheck = precheckByCurrency?.[normalizeHyperpayPayCurrency(currency)];
  return precheck?.sufficient === false;
}

export function normalizeHyperpayPayCurrency(value) {
  const raw = String(value || "").trim().toUpperCase();
  if (raw === HYPERPAY_PAY_CURRENCIES.BRL) return HYPERPAY_PAY_CURRENCIES.BRL;
  return HYPERPAY_PAY_CURRENCIES.USDT;
}

export function resolveHyperpayPayCurrencies(cardInfo) {
  const fromApi = cardInfo?.payCurrencies ?? cardInfo?.pay_currencies;
  if (Array.isArray(fromApi) && fromApi.length) {
    const normalized = fromApi
      .map((item) => normalizeHyperpayPayCurrency(item))
      .filter((item, index, arr) => arr.indexOf(item) === index);
    if (normalized.length) return normalized;
  }
  return [DEFAULT_HYPERPAY_PAY_CURRENCY];
}

export function resolveDefaultHyperpayPayCurrency(cardInfo) {
  return normalizeHyperpayPayCurrency(
    cardInfo?.defaultPayCurrency ??
      cardInfo?.default_pay_currency ??
      DEFAULT_HYPERPAY_PAY_CURRENCY
  );
}

export function resolveApplyPrecheckPayCurrency(precheck, fallback = DEFAULT_HYPERPAY_PAY_CURRENCY) {
  return normalizeHyperpayPayCurrency(precheck?.payCurrency ?? precheck?.pay_currency ?? fallback);
}

export function formatPayCurrencyAmountLabel(value, payCurrency) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  const amount = formatUsdtAmount(n);
  const currency = normalizeHyperpayPayCurrency(payCurrency);
  if (currency === HYPERPAY_PAY_CURRENCIES.BRL) return `${amount} BRL`;
  return `${amount}U`;
}

export function formatPayCurrencyDisplay(value, payCurrency) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  const amount = n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 8,
  });
  return `${amount} ${normalizeHyperpayPayCurrency(payCurrency)}`;
}

/** 开卡费原价（USDT），免开卡费时用于划线展示 */
export const SPIKEX_CARD_ORIGINAL_ISSUED_FEE_USDT = 20;

/** entry/status 枚举（接口文档 §一） */
export const HYPERPAY_ENTRY_STATUSES = new Set([
  "LEVEL_LOCKED",
  "NO_CARD",
  "REVIEWING",
  "WAIT_ATTACHMENT",
  "REJECTED",
  "ACTIVE",
  "FROZEN",
]);

/** apply/status 全量枚举（接口 POST apply/status） */
export const HYPERPAY_APPLY_STATUSES = new Set([
  "FREEZING",
  "REVIEWING",
  "WAIT_ATTACHMENT",
  "APPROVED",
  "ACTIVATING",
  "ACTIVE",
  "REJECTED",
  "ACTIVATE_REJECTED",
  "CANCELLED",
]);

/** apply/status 子状态（entry=REVIEWING 时的细化展示） */
export const HYPERPAY_APPLY_SUB_STATUSES = new Set([
  "FREEZING",
  "REVIEWING",
  "APPROVED",
  "ACTIVATING",
]);

/** 四大申卡展示阶段 */
export const SPIKEX_CARD_APPLY_PHASES = {
  NONE: "none",
  APPLYING: "applying",
  ATTACHMENT: "attachment",
  FAILED: "failed",
  ACTIVE: "active",
  FROZEN: "frozen",
};

/** entry/status → 四大阶段 + 持卡管理阶段 */
const ENTRY_STATUS_PHASE_MAP = {
  NO_CARD: SPIKEX_CARD_APPLY_PHASES.NONE,
  REVIEWING: SPIKEX_CARD_APPLY_PHASES.APPLYING,
  WAIT_ATTACHMENT: SPIKEX_CARD_APPLY_PHASES.ATTACHMENT,
  REJECTED: SPIKEX_CARD_APPLY_PHASES.FAILED,
  ACTIVE: SPIKEX_CARD_APPLY_PHASES.ACTIVE,
  FROZEN: SPIKEX_CARD_APPLY_PHASES.FROZEN,
};

/** entry/status 直接对应的 UI（无 apply 子状态时） */
const ENTRY_STATUS_UI_MAP = {
  REVIEWING: {
    icon: "success",
    titleKey: "spikexCard.statusReviewingTitle",
    descKeys: ["spikexCard.statusReviewingDesc"],
    showApplyId: true,
    footer: "waitlist",
  },
  WAIT_ATTACHMENT: {
    icon: "warning",
    titleKey: "spikexCard.statusAttachmentTitle",
    descKeys: ["spikexCard.statusAttachmentDesc"],
    footer: "submitAttachment",
    footerLabelKey: "spikexCard.submitAttachment",
  },
  REJECTED: {
    icon: "error",
    titleKey: "spikexCard.statusRejectedTitle",
    descKeys: ["spikexCard.statusRejectedDesc"],
    showRejectReason: true,
    footer: "reapply",
    footerLabelKey: "spikexCard.reapplyNow",
  },
};

/** apply/status 子状态 UI（entry=REVIEWING 时的细化展示） */
const APPLY_SUB_STATUS_UI_MAP = {
  FREEZING: {
    icon: "success",
    titleKey: "spikexCard.statusFreezingTitle",
    descKeys: ["spikexCard.statusFreezingDesc"],
    footer: "processing",
    footerLabelKey: "spikexCard.statusProcessingBtn",
  },
  REVIEWING: ENTRY_STATUS_UI_MAP.REVIEWING,
  APPROVED: {
    icon: "success",
    titleKey: "spikexCard.statusApprovedTitle",
    descKeys: ["spikexCard.statusApprovedDesc"],
    footer: "processing",
    footerLabelKey: "spikexCard.statusCardProcessingBtn",
  },
  ACTIVATING: {
    icon: "success",
    titleKey: "spikexCard.statusActivatingTitle",
    descKeys: ["spikexCard.statusActivatingDesc"],
    footer: "processing",
    footerLabelKey: "spikexCard.statusActivatingBtn",
  },
};

/**
 * 解析申卡四大阶段（基于 entry/status）
 * @param {object|null} entryStatus
 */
export function resolveSpikexCardApplyPhase(entryStatus) {
  const status = entryStatus?.status;
  if (!status || status === "LEVEL_LOCKED") return SPIKEX_CARD_APPLY_PHASES.NONE;
  return ENTRY_STATUS_PHASE_MAP[status] || SPIKEX_CARD_APPLY_PHASES.NONE;
}

/**
 * 解析当前状态的 UI 配置
 * @param {object|null} entryStatus — GET entry/status result
 * @param {object|null} applyStatus — GET apply/status result（补传/细化用）
 */
export function resolveEntryStatusUi(entryStatus, applyStatus) {
  const status = entryStatus?.status;
  if (!status || status === "NO_CARD" || status === "LEVEL_LOCKED") return null;

  if (status === "REVIEWING") {
    const sub = applyStatus?.applyStatus;
    if (sub && APPLY_SUB_STATUS_UI_MAP[sub]) {
      return APPLY_SUB_STATUS_UI_MAP[sub];
    }
    return ENTRY_STATUS_UI_MAP.REVIEWING;
  }

  return ENTRY_STATUS_UI_MAP[status] || null;
}

/** @deprecated 使用 resolveEntryStatusUi */
export function resolveApplyStatusUi(entryStatus, applyStatus) {
  return resolveEntryStatusUi(entryStatus, applyStatus);
}

/**
 * 是否满足开卡资格（读 GET /hyperpay/card/eligibility）
 * - eligible === true
 * - 有 requiredLevel 时再校验 currentLevel >= requiredLevel
 *
 * @param {object|null|undefined} eligibility
 */
export function resolveSpikexCardApplyEligibility(eligibility) {
  if (!eligibility || typeof eligibility !== "object") return false;
  if (eligibility.eligible !== true) return false;

  const requiredLevel = Number(eligibility.requiredLevel);
  if (!Number.isFinite(requiredLevel)) return true;

  const currentLevel = Number(eligibility.currentLevel);
  if (!Number.isFinite(currentLevel)) return false;
  return currentLevel >= requiredLevel;
}

/**
 * 卡片左上角「Unlock at Lv.X」展示等级（读 eligibility.requiredLevel）
 */
export function resolveSpikexCardDisplayUnlockLevel(eligibility) {
  const fromApi = Number(eligibility?.requiredLevel);
  if (Number.isFinite(fromApi) && fromApi >= 0) {
    return fromApi;
  }
  return SPIKEX_CARD_UNLOCK_LEVEL;
}

/**
 * 是否已解锁开卡（非锁定页）
 * @param {object|null} entryStatus
 * @param {object|null} eligibility
 */
export function resolveSpikexCardUnlocked(entryStatus, eligibility) {
  const status = entryStatus?.status;
  if (
    status === "REVIEWING" ||
    status === "WAIT_ATTACHMENT" ||
    status === "REJECTED" ||
    status === "ACTIVE" ||
    status === "FROZEN"
  ) {
    return true;
  }
  return resolveSpikexCardApplyEligibility(eligibility);
}

/**
 * 页面状态：1=等级锁定，2=可申请/可重申，3=申请进行中或已持卡
 * @param {object|null} entryStatus
 * @param {object|null} [eligibility]
 */
export function resolveSpikexCardPageStatus(entryStatus, eligibility) {
  const status = entryStatus?.status;
  if (!resolveSpikexCardApplyEligibility(eligibility)) return 1;
  if (!status || status === "NO_CARD" || status === "REJECTED" || status === "LEVEL_LOCKED") return 2;
  return 3;
}

function pickFirstDefined(source, keys) {
  if (!source || typeof source !== "object") return undefined;
  for (const key of keys) {
    const value = source[key];
    if (value != null && value !== "") return value;
  }
  return undefined;
}

/** 归一化 §三-8 balance 响应（兼容 camelCase / snake_case / 未解包 result） */
export function normalizeHyperpayCardBalance(raw) {
  if (!raw || typeof raw !== "object") return null;

  let source = raw;
  if (raw.result && typeof raw.result === "object" && !Array.isArray(raw.result)) {
    source = raw.result;
  }

  const availableBalance = pickFirstDefined(source, [
    "availableBalance",
    "available_balance",
    "balance",
    "currentBalance",
    "current_balance",
  ]);
  const currentBalance = pickFirstDefined(source, [
    "currentBalance",
    "current_balance",
    "availableBalance",
    "available_balance",
    "balance",
  ]);
  const cardCurrency = pickFirstDefined(source, ["cardCurrency", "card_currency"]) || "usd";
  const cardNumber = pickFirstDefined(source, ["cardNumber", "card_number"]);
  const cardType = pickFirstDefined(source, ["cardType", "card_type"]);
  const cardStatus = pickFirstDefined(source, ["cardStatus", "card_status"]);

  if (
    availableBalance == null &&
    currentBalance == null &&
    cardNumber == null &&
    cardCurrency == null
  ) {
    return null;
  }

  return {
    availableBalance: availableBalance != null ? String(availableBalance) : undefined,
    currentBalance: currentBalance != null ? String(currentBalance) : undefined,
    cardCurrency: String(cardCurrency),
    cardNumber: cardNumber != null ? String(cardNumber) : undefined,
    cardType: cardType != null ? String(cardType) : undefined,
    cardStatus: cardStatus != null ? String(cardStatus) : undefined,
  };
}

export function resolveHyperpayCardAvailableBalance(cardBalance) {
  if (!cardBalance) return null;
  if (cardBalance.availableBalance != null && cardBalance.availableBalance !== "") {
    return cardBalance.availableBalance;
  }
  const normalized = normalizeHyperpayCardBalance(cardBalance);
  return normalized?.availableBalance ?? normalized?.currentBalance ?? null;
}

/**
 * 卡片余额展示：优先 balance 接口，ACTIVE 时回退 entry/status.balance
 * @param {object|null} cardBalance
 * @param {object|null} entryStatus
 */
export function resolveDisplayCardBalance(cardBalance, entryStatus) {
  const fromBalance = resolveHyperpayCardAvailableBalance(cardBalance);
  if (fromBalance != null) return fromBalance;
  if (entryStatus?.status === "ACTIVE" && entryStatus?.balance != null) {
    return entryStatus.balance;
  }
  return null;
}

/** 展示用申请编号（优先 entry.applyId） */
export function formatApplyReference(entryStatus, applyStatus) {
  const id = entryStatus?.applyId ?? applyStatus?.applyId ?? applyStatus?.mcTradeNo;
  if (id == null || id === "") return "";
  return String(id);
}

/** 状态卡片附加上下文 */
export function buildStatusDisplayContext(entryStatus, applyStatus) {
  return {
    applyId: entryStatus?.applyId ?? applyStatus?.applyId,
    rejectReason: entryStatus?.rejectReason ?? applyStatus?.failReason,
    attachmentHint:
      entryStatus?.status === "WAIT_ATTACHMENT" && applyStatus?.failReason
        ? applyStatus.failReason
        : null,
    mcTradeNo: applyStatus?.mcTradeNo,
    balance: entryStatus?.balance,
  };
}

/**
 * 已解锁用户的内容区配置
 */
export function resolvePostApplyView(applyPhase, statusUi) {
  if (applyPhase === SPIKEX_CARD_APPLY_PHASES.NONE) {
    return {
      showLevel: true,
      showQualification: true,
      showApplyProcess: true,
      showStatusCard: false,
      showAttachmentForm: false,
      showManageView: false,
      footer: "apply",
    };
  }

  if (applyPhase === SPIKEX_CARD_APPLY_PHASES.APPLYING) {
    return {
      showLevel: false,
      showQualification: false,
      showApplyProcess: false,
      showStatusCard: true,
      showAttachmentForm: false,
      showManageView: false,
      footer: statusUi?.footer || "waitlist",
      footerLabelKey: statusUi?.footerLabelKey,
    };
  }

  if (applyPhase === SPIKEX_CARD_APPLY_PHASES.ATTACHMENT) {
    return {
      showLevel: false,
      showQualification: false,
      showApplyProcess: false,
      showStatusCard: true,
      showAttachmentForm: true,
      showManageView: false,
      footer: "submitAttachment",
      footerLabelKey: statusUi?.footerLabelKey || "spikexCard.submitAttachment",
    };
  }

  if (applyPhase === SPIKEX_CARD_APPLY_PHASES.FAILED) {
    return {
      showLevel: false,
      showQualification: false,
      showApplyProcess: false,
      showStatusCard: true,
      showAttachmentForm: false,
      showManageView: false,
      footer: "reapply",
      footerLabelKey: statusUi?.footerLabelKey || "spikexCard.reapplyNow",
    };
  }

  if (applyPhase === SPIKEX_CARD_APPLY_PHASES.ACTIVE) {
    return {
      showLevel: false,
      showQualification: false,
      showApplyProcess: false,
      showStatusCard: false,
      showAttachmentForm: false,
      showManageView: true,
      manageVariant: "active",
      footer: "none",
    };
  }

  if (applyPhase === SPIKEX_CARD_APPLY_PHASES.FROZEN) {
    return {
      showLevel: false,
      showQualification: false,
      showApplyProcess: false,
      showStatusCard: false,
      showAttachmentForm: false,
      showManageView: true,
      manageVariant: "frozen",
      footer: "none",
    };
  }

  return null;
}

/** 卡号后四位 */
export function formatCardLast4(cardNumber) {
  const digits = String(cardNumber || "").replace(/\D/g, "");
  if (digits.length < 4) return "----";
  return digits.slice(-4);
}

/** 脱敏卡号展示 */
export function formatMaskedCardNumber(cardNumber) {
  const last4 = formatCardLast4(cardNumber);
  return `**** **** **** ${last4}`;
}

/** 完整卡号分组 */
export function formatGroupedCardNumber(cardNumber) {
  const digits = String(cardNumber || "").replace(/\D/g, "");
  if (!digits) return "";
  return digits.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
}

/** USD 余额展示 */
export function formatCardBalanceDisplay(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.00";
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 3 });
}

/** HyperPay 充值订单状态（§四-15） */
export const HYPERPAY_RECHARGE_STATUS = {
  PROCESSING: 0,
  SUCCESS: 1,
  FAILED: 2,
  PENDING_REFUND: 3,
  REFUNDED: 4,
};

/** HyperPay 充值手续费：优先 rechargeFeeUsdtAmount，其次 rechargeFeeAmount */
export function resolveHyperpayRechargeFeeAmount(estimate) {
  if (!estimate || typeof estimate !== "object") return null;
  const raw =
    estimate.rechargeFeeUsdtAmount ??
    estimate.recharge_fee_usdt_amount ??
    estimate.rechargeFeeAmount ??
    estimate.recharge_fee_amount;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function pickEstimateAmount(estimate, ...keys) {
  if (!estimate || typeof estimate !== "object") return null;
  for (const key of keys) {
    const raw = estimate[key];
    if (raw == null || raw === "") continue;
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/** 充值估算：用户需支付金额（estimate/crypto → payAmount 或 totalCoinAmount） */
export function resolveHyperpayRechargePayAmount(estimate) {
  const payAmount = pickEstimateAmount(estimate, "payAmount", "pay_amount");
  if (payAmount != null) return payAmount;
  return resolveHyperpayRechargePayUsdt(estimate);
}

/** 充值估算/结果：用户扣款币种 */
export function resolveHyperpayRechargePayCurrency(estimate, fallback = DEFAULT_HYPERPAY_PAY_CURRENCY) {
  const raw = estimate?.payCurrency ?? estimate?.pay_currency;
  if (raw != null && String(raw).trim()) {
    return normalizeHyperpayPayCurrency(raw);
  }
  return normalizeHyperpayPayCurrency(fallback);
}

/** 充值估算：汇率 1 USDT = N payCurrency */
export function resolveHyperpayRechargeExchangeRate(estimate) {
  return pickEstimateAmount(estimate, "exchangeRate", "exchange_rate");
}

/** 充值估算：转化费（BRL 场景恒为 0） */
export function resolveHyperpayRechargeConvertFeeAmount(estimate) {
  const raw = estimate?.convertFeeAmount ?? estimate?.convert_fee_amount;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

/** 充值估算：用户需支付 USDT（estimate/crypto → totalCoinAmount） */
export function resolveHyperpayRechargePayUsdt(estimate) {
  return pickEstimateAmount(
    estimate,
    "totalCoinAmount",
    "total_coin_amount",
    "coinAmount",
    "coin_amount"
  );
}

/** 充值估算/结果：卡到账 USD 展示字段（estimate/currency、recharge → currencyAmount） */
export function resolveHyperpayRechargeReceiveUsd(estimate, fallback) {
  const fromEstimate = pickEstimateAmount(
    estimate,
    "currencyAmount",
    "currency_amount"
  );
  if (fromEstimate != null) return fromEstimate;
  if (fallback == null || fallback === "") return null;
  const n = Number(fallback);
  return Number.isFinite(n) ? n : null;
}

/** @deprecated 请使用 resolveHyperpayRechargeReceiveUsd（currencyAmount） */
export function resolveHyperpayRechargeRealReceiveUsd(estimate, fallback) {
  return resolveHyperpayRechargeReceiveUsd(estimate, fallback);
}

/** 卡到账 USD 展示/下单：截断保留 2 位小数（不四舍五入） */
export function truncateHyperpayRechargeReceiveUsd(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.trunc(n * 100) / 100;
}

/** 充值页 Deposit fee rate：优先估算响应，否则 cardInfo */
export function resolveRechargeDepositFeeRate(estimate, cardInfo) {
  const fromEstimate = resolveRechargeEstimateDepositFeeRate(estimate);
  if (fromEstimate != null) return fromEstimate;
  return resolveCardInfoTopUpFeeRate(cardInfo);
}

/** 充值估算/结果：支付币种（pay_coin，默认 usdt） */
export function resolveHyperpayPayCoin(estimate) {
  const raw = estimate?.pay_coin ?? estimate?.payCoin;
  return raw != null && String(raw).trim() ? String(raw).trim().toLowerCase() : "usdt";
}

/** 充值估算/结果：卡法币币种（card_coin，默认 usd） */
export function resolveHyperpayCardCoin(estimate) {
  const raw = estimate?.card_coin ?? estimate?.cardCoin;
  return raw != null && String(raw).trim() ? String(raw).trim().toLowerCase() : "usd";
}

/** 交易记录：卡面展示金额（优先 currency_amount，否则 tx_amount） */
export function resolveTransactionCardAmount(raw) {
  if (!raw || typeof raw !== "object") return null;
  const currencyAmount = pickEstimateAmount(raw, "currency_amount", "currencyAmount");
  if (currencyAmount != null) return currencyAmount;
  return pickEstimateAmount(raw, "tx_amount", "txAmount");
}

/** 交易记录：卡面展示币种（有 currency_amount 时优先 card_coin，否则 tx_currency） */
export function resolveTransactionCardCoin(raw) {
  if (!raw || typeof raw !== "object") return "usd";
  const hasCurrencyAmount =
    pickEstimateAmount(raw, "currency_amount", "currencyAmount") != null;
  if (hasCurrencyAmount) {
    return resolveHyperpayCardCoin(raw);
  }
  const txCurrency = raw.tx_currency ?? raw.txCurrency;
  if (txCurrency != null && String(txCurrency).trim()) {
    return String(txCurrency).trim().toLowerCase();
  }
  return resolveHyperpayCardCoin(raw);
}

/** 交易记录：用户支付金额（tx_amount，充值场景多为 USDT） */
export function resolveTransactionPayAmount(raw) {
  return pickEstimateAmount(raw, "tx_amount", "txAmount");
}

/** 归一化 §四-13 充值限额响应 */
export function normalizeHyperpayRechargeLimit(raw) {
  if (!raw || typeof raw !== "object") return null;
  const source = raw.result && typeof raw.result === "object" ? raw.result : raw;
  return {
    maxSingleAmount: source.maxSingleAmount ?? source.max_single_amount,
    minSingleAmount: source.minSingleAmount ?? source.min_single_amount,
    dailyLimit: source.dailyLimit ?? source.daily_limit,
    dailyUsed: source.dailyUsed ?? source.daily_used,
    dailyRemaining: source.dailyRemaining ?? source.daily_remaining,
    payCoin: source.payCoin ?? source.pay_coin,
    rechargeFeeRate: source.rechargeFeeRate ?? source.recharge_fee_rate,
  };
}

/** 归一化 §六-18 资金流水单条记录 */
export function normalizeHyperpayFundRecord(raw) {
  if (!raw || typeof raw !== "object") return null;
  const amount = raw.amount;
  const remark = raw.remark;
  const billType = raw.billType ?? raw.bill_type;
  const state = raw.state;
  const createdTime = raw.createdTime ?? raw.created_time;
  const currencyId = raw.currencyId ?? raw.currency_id;
  if (amount == null && remark == null) return null;
  return {
    amount: String(amount ?? ""),
    currencyId: currencyId != null ? String(currencyId) : undefined,
    remark: String(remark ?? ""),
    billType: billType != null ? Number(billType) : undefined,
    state: state != null ? Number(state) : undefined,
    createdTime,
  };
}

/** 从 §六-18 响应中提取 items 列表 */
export function extractHyperpayFundItems(payload) {
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.result?.items)) return payload.result.items;
  if (Array.isArray(payload)) return payload;
  return [];
}

/** 从充值提交/查询结果解析轮询用的 mc_trade_no */
export function resolveRechargeQueryTradeNo(rechargeResult) {
  if (!rechargeResult || typeof rechargeResult !== "object") return "";
  return String(
    rechargeResult.mc_trade_no ?? rechargeResult.mcTradeNo ?? rechargeResult.orderNo ?? ""
  ).trim();
}

export function isRechargeTerminalStatus(status) {
  const value = Number(status);
  return (
    value === HYPERPAY_RECHARGE_STATUS.SUCCESS ||
    value === HYPERPAY_RECHARGE_STATUS.FAILED ||
    value === HYPERPAY_RECHARGE_STATUS.REFUNDED
  );
}

/** HyperPay 交易业务类型 */
export const HYPERPAY_TX_BUSINESS_TYPES = {
  SPEND: 1,
  RECHARGE: 2,
  WITHDRAW: 3,
  REFUND: 8,
  FEE: 10,
  BUY: 101,
  CLOSE_CARD: 102,
};

/** §五-16 支持的全部 business_type */
export const HYPERPAY_TX_ALL_BUSINESS_TYPES = [
  HYPERPAY_TX_BUSINESS_TYPES.SPEND,
  HYPERPAY_TX_BUSINESS_TYPES.RECHARGE,
  HYPERPAY_TX_BUSINESS_TYPES.WITHDRAW,
  HYPERPAY_TX_BUSINESS_TYPES.REFUND,
  HYPERPAY_TX_BUSINESS_TYPES.FEE,
  HYPERPAY_TX_BUSINESS_TYPES.BUY,
  HYPERPAY_TX_BUSINESS_TYPES.CLOSE_CARD,
];

/** 列表查询默认合并的业务类型（兼容旧逻辑） */
export const HYPERPAY_TX_LIST_BUSINESS_TYPES = HYPERPAY_TX_ALL_BUSINESS_TYPES;

/** 交易记录页 Tab（按 §五-16 business_type 分类，不含「全部」） */
export const HYPERPAY_TX_TAB_OPTIONS = [
  { key: "1", labelKey: "spikexCard.txBusinessSpend", businessTypes: [HYPERPAY_TX_BUSINESS_TYPES.SPEND] },
  { key: "2", labelKey: "spikexCard.txBusinessRecharge", businessTypes: [HYPERPAY_TX_BUSINESS_TYPES.RECHARGE] },
  { key: "3", labelKey: "spikexCard.txBusinessWithdraw", businessTypes: [HYPERPAY_TX_BUSINESS_TYPES.WITHDRAW] },
  { key: "8", labelKey: "spikexCard.txBusinessRefund", businessTypes: [HYPERPAY_TX_BUSINESS_TYPES.REFUND] },
  { key: "10", labelKey: "spikexCard.txBusinessFee", businessTypes: [HYPERPAY_TX_BUSINESS_TYPES.FEE] },
  { key: "101", labelKey: "spikexCard.txBusinessBuy", businessTypes: [HYPERPAY_TX_BUSINESS_TYPES.BUY] },
  { key: "102", labelKey: "spikexCard.txBusinessCloseCard", businessTypes: [HYPERPAY_TX_BUSINESS_TYPES.CLOSE_CARD] },
];

export const HYPERPAY_TX_DEFAULT_TAB_KEY = String(HYPERPAY_TX_BUSINESS_TYPES.SPEND);

export function resolveHyperpayTxTabBusinessTypes(tabKey) {
  const tab = HYPERPAY_TX_TAB_OPTIONS.find((item) => item.key === tabKey);
  return tab?.businessTypes ?? [HYPERPAY_TX_BUSINESS_TYPES.SPEND];
}

/** 解析交易页 mock：1/form=mock 数据 */
export function parseMockTransactionsMode(search) {
  const params = new URLSearchParams(search || "");
  const mode = String(params.get("mockTransactions") || "").trim().toLowerCase();
  if (mode === "1" || mode === "form" || mode === "true") return "form";
  return null;
}

function formatTxDateTimeFromUnix(value) {
  const ts = Number(value);
  if (!Number.isFinite(ts) || ts <= 0) return "—";
  const date = new Date(ts * 1000);
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function formatTxDateTimeDetailFromUnix(value) {
  const ts = Number(value);
  if (!Number.isFinite(ts) || ts <= 0) return "—";
  const date = new Date(ts * 1000);
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

export function formatTransactionListDateTime(value) {
  if (value == null || value === "") return "—";
  if (typeof value === "number" || /^\d+$/.test(String(value))) {
    return formatTxDateTimeFromUnix(value);
  }
  return String(value);
}

export function formatTransactionDetailDateTime(value) {
  if (value == null || value === "") return "—";
  if (typeof value === "number" || /^\d+$/.test(String(value))) {
    return formatTxDateTimeDetailFromUnix(value);
  }
  return String(value);
}

export function resolveTransactionBusinessTypeKey(businessType) {
  const type = Number(businessType);
  if (type === HYPERPAY_TX_BUSINESS_TYPES.RECHARGE) return "spikexCard.txBusinessRecharge";
  if (type === HYPERPAY_TX_BUSINESS_TYPES.SPEND) return "spikexCard.txBusinessSpend";
  if (type === HYPERPAY_TX_BUSINESS_TYPES.REFUND) return "spikexCard.txBusinessRefund";
  if (type === HYPERPAY_TX_BUSINESS_TYPES.WITHDRAW) return "spikexCard.txBusinessWithdraw";
  if (type === HYPERPAY_TX_BUSINESS_TYPES.FEE) return "spikexCard.txBusinessFee";
  if (type === HYPERPAY_TX_BUSINESS_TYPES.BUY) return "spikexCard.txBusinessBuy";
  if (type === HYPERPAY_TX_BUSINESS_TYPES.CLOSE_CARD) return "spikexCard.txBusinessCloseCard";
  return "spikexCard.txBusinessOther";
}

export function resolveTransactionStatusKey(txStatus) {
  const status = Number(txStatus);
  if (status === 5 || status === -1) return "spikexCard.transactionFailed";
  if (status === 2 || status === 3 || status === 0) return "spikexCard.transactionProcessing";
  if (status === 4) return "spikexCard.transactionRefunded";
  if (status === 1) return "spikexCard.transactionCompleted";
  return "spikexCard.transactionCompleted";
}

export function resolveTransactionStatusTone(txStatus) {
  const status = Number(txStatus);
  if (status === 5 || status === -1) return "failed";
  if (status === 2 || status === 3 || status === 0) return "processing";
  if (status === 4) return "completed";
  return "completed";
}

export function resolveTransactionBadgeKey(businessType) {
  const type = Number(businessType);
  if (type === HYPERPAY_TX_BUSINESS_TYPES.RECHARGE) return "spikexCard.txBadgeDeposit";
  if (type === HYPERPAY_TX_BUSINESS_TYPES.SPEND) return "spikexCard.txBadgeSpend";
  return null;
}

export function resolveTransactionBadgeTone(businessType) {
  const type = Number(businessType);
  if (type === HYPERPAY_TX_BUSINESS_TYPES.RECHARGE) return "deposit";
  return "spend";
}

export function formatTransactionAmountDisplay(txAmount, businessType, txCurrency = "USD") {
  const amount = Number(txAmount);
  if (!Number.isFinite(amount)) return { signed: "—", detail: "—" };
  const coin = String(txCurrency || "usd").trim().toLowerCase();
  const abs = Math.abs(amount).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const type = Number(businessType);
  const isCredit = type === HYPERPAY_TX_BUSINESS_TYPES.RECHARGE || type === HYPERPAY_TX_BUSINESS_TYPES.REFUND;
  const sign = isCredit ? (amount >= 0 ? "+" : "-") : amount >= 0 ? "-" : "+";
  if (coin === "usd") {
    return {
      signed: `${sign}$${abs}`,
      detail: `${sign}$${abs} USD`,
    };
  }
  if (coin === "usdt") {
    return {
      signed: `${sign}${abs} USDT`,
      detail: `${sign}${abs} USDT`,
    };
  }
  const label = coin.toUpperCase();
  return {
    signed: `${sign}${abs} ${label}`,
    detail: `${sign}${abs} ${label}`,
  };
}

export function formatTransactionFeeDisplay(fee, txCurrency = "USD") {
  const amount = Number(fee);
  if (!Number.isFinite(amount)) return "—";
  const coin = String(txCurrency || "usd").trim().toLowerCase();
  const abs = amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (coin === "usd") return `${abs} USD`;
  if (coin === "usdt") return `${abs} USDT`;
  return `${abs} ${coin.toUpperCase()}`;
}

export function normalizeTransactionRecord(raw, businessTypeFallback) {
  if (!raw || typeof raw !== "object") return null;
  const businessType = Number(raw.businessType ?? raw.business_type ?? businessTypeFallback);
  const txAmount = raw.txAmount ?? raw.tx_amount;
  const txCurrency = raw.txCurrency ?? raw.tx_currency ?? "USD";
  const cardAmount = resolveTransactionCardAmount(raw);
  const cardCoin = resolveTransactionCardCoin(raw);
  const payAmount = resolveTransactionPayAmount(raw);
  const payCoin = resolveHyperpayPayCoin(raw);
  const txStatus = raw.txStatus ?? raw.tx_status ?? 1;
  const txId = raw.txId ?? raw.tx_id ?? raw.id;
  const txTime = raw.tx_time ?? raw.txTime ?? raw.transaction_date ?? raw.transactionDate;
  const description = raw.description ?? raw.txDescription ?? "";
  const title =
    businessType === HYPERPAY_TX_BUSINESS_TYPES.RECHARGE && !description
      ? null
      : description || null;

  return {
    txId: String(txId || ""),
    businessType,
    txAmount: String(txAmount ?? ""),
    txCurrency,
    currencyAmount: cardAmount != null ? String(cardAmount) : "",
    cardCoin,
    payAmount: payAmount != null ? String(payAmount) : "",
    payCoin,
    txStatus,
    txTime,
    description: String(description || ""),
    title,
    statusKey: resolveTransactionStatusKey(txStatus),
    statusTone: resolveTransactionStatusTone(txStatus),
    badgeKey: resolveTransactionBadgeKey(businessType),
    badgeTone: resolveTransactionBadgeTone(businessType),
    businessTypeKey: resolveTransactionBusinessTypeKey(businessType),
    datetime: formatTransactionListDateTime(txTime),
    amountDisplay: formatTransactionAmountDisplay(cardAmount, businessType, cardCoin),
  };
}

export function normalizeTransactionDetail(raw) {
  if (!raw || typeof raw !== "object") return null;
  const businessType = Number(raw.businessType ?? raw.business_type);
  const txAmount = raw.txAmount ?? raw.tx_amount;
  const txCurrency = raw.txCurrency ?? raw.tx_currency ?? "USD";
  const cardAmount = resolveTransactionCardAmount(raw);
  const cardCoin = resolveTransactionCardCoin(raw);
  const payAmount = resolveTransactionPayAmount(raw);
  const payCoin = resolveHyperpayPayCoin(raw);
  const txStatus = raw.txStatus ?? raw.tx_status ?? 1;
  const txId = raw.txId ?? raw.tx_id;
  const txTime = raw.tx_time ?? raw.txTime ?? raw.transaction_date ?? raw.transactionDate;
  const description = raw.description ?? "";
  const fee = raw.fee ?? raw.txFee ?? raw.tx_fee;
  const amountDisplay = formatTransactionAmountDisplay(cardAmount, businessType, cardCoin);

  return {
    txId: String(txId || ""),
    businessType,
    txAmount: String(txAmount ?? ""),
    txCurrency,
    currencyAmount: cardAmount != null ? String(cardAmount) : "",
    cardCoin,
    payAmount: payAmount != null ? String(payAmount) : "",
    payCoin,
    txStatus,
    txTime,
    description: String(description || ""),
    fee: fee != null ? String(fee) : "",
    statusKey: resolveTransactionStatusKey(txStatus),
    statusTone: resolveTransactionStatusTone(txStatus),
    businessTypeKey: resolveTransactionBusinessTypeKey(businessType),
    datetime: formatTransactionDetailDateTime(txTime),
    amountDetail: amountDisplay.detail,
    feeDisplay: formatTransactionFeeDisplay(fee, cardCoin),
    title: description || null,
  };
}

/** 交易记录列表 mock（对齐 Figma） */
export function buildMockTransactionList() {
  const baseTs = Math.floor(new Date("2026-06-10T14:32:00").getTime() / 1000);
  return [
    {
      txId: "TX_MOCK_001",
      businessType: HYPERPAY_TX_BUSINESS_TYPES.RECHARGE,
      txAmount: "31.49",
      txCurrency: "USDT",
      currencyAmount: "29.99",
      cardCoin: "usd",
      payCoin: "usdt",
      txStatus: 1,
      txTime: baseTs,
      description: "",
      fee: "0",
    },
    {
      txId: "TX_MOCK_002",
      businessType: HYPERPAY_TX_BUSINESS_TYPES.SPEND,
      txAmount: "29.99",
      txCurrency: "USD",
      txStatus: 1,
      txTime: baseTs,
      description: "Uber",
      fee: "0.5",
    },
    {
      txId: "TX_MOCK_003",
      businessType: HYPERPAY_TX_BUSINESS_TYPES.SPEND,
      txAmount: "29.99",
      txCurrency: "USD",
      txStatus: 1,
      txTime: baseTs,
      description: "Uber",
      fee: "0.5",
    },
    {
      txId: "TX_MOCK_004",
      businessType: HYPERPAY_TX_BUSINESS_TYPES.RECHARGE,
      txAmount: "-31.49",
      txCurrency: "USDT",
      currencyAmount: "-29.99",
      cardCoin: "usd",
      payCoin: "usdt",
      txStatus: 5,
      txTime: baseTs,
      description: "Uber",
      fee: "0",
    },
  ]
    .map((item) => normalizeTransactionRecord(item))
    .filter(Boolean);
}

export function buildMockTransactionDetail(txId) {
  const list = buildMockTransactionList();
  const item = list.find((row) => row.txId === txId) || list[1] || list[0];
  if (!item) return null;
  const description =
    item.description ||
    (item.businessType === HYPERPAY_TX_BUSINESS_TYPES.RECHARGE ? "" : "Amazon.com");
  return normalizeTransactionDetail({
    txId: item.txId,
    businessType: item.businessType,
    tx_amount: item.payAmount || item.txAmount,
    pay_coin: item.payCoin,
    currency_amount: item.currencyAmount || item.txAmount,
    card_coin: item.cardCoin || item.txCurrency,
    tx_currency: item.txCurrency,
    txStatus: item.txStatus,
    txTime: item.txTime,
    description,
    fee: item.businessType === HYPERPAY_TX_BUSINESS_TYPES.SPEND ? "0.5" : "0",
  });
}

/** §五-16 交易记录查询时间，UNIX 秒级时间戳字符串 */
function toHyperpayTransactionUnixSeconds(value) {
  if (value == null || value === "") return undefined;

  let date;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === "number" && Number.isFinite(value)) {
    date = new Date(value);
  } else {
    const raw = String(value).trim();
    if (/^\d+$/.test(raw)) {
      const numeric = Number(raw);
      return String(raw.length <= 10 ? Math.floor(numeric) : Math.floor(numeric / 1000));
    }
    date = new Date(raw);
  }

  if (Number.isNaN(date.getTime())) return undefined;
  return String(Math.floor(date.getTime() / 1000));
}

/** §五-16 交易记录查询时间范围（UNIX 秒级时间戳，跨度不超过 60 天） */
export function buildHyperpayTransactionDateRange({ start, end, maxSpanDays = 60 } = {}) {
  const endDate = end instanceof Date ? new Date(end) : new Date();
  endDate.setHours(23, 59, 59, 999);

  let startDate;
  if (start instanceof Date) {
    startDate = new Date(start);
    startDate.setHours(0, 0, 0, 0);
  } else {
    startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - (maxSpanDays - 1));
    startDate.setHours(0, 0, 0, 0);
  }

  const maxSpanMs = maxSpanDays * 24 * 60 * 60 * 1000;
  if (endDate.getTime() - startDate.getTime() > maxSpanMs) {
    startDate = new Date(endDate.getTime() - maxSpanMs);
    startDate.setHours(0, 0, 0, 0);
  }

  return {
    start_time: toHyperpayTransactionUnixSeconds(startDate),
    end_time: toHyperpayTransactionUnixSeconds(endDate),
  };
}

/** cardInfo.card_type_id — HyperPay 卡种 ID（§二-4，用于充值估算等） */
export function resolveHyperpayCardTypeId(cardInfo) {
  const raw =
    cardInfo?.cardTypeId ??
    cardInfo?.card_type_id ??
    cardInfo?.cardTypeID;
  const value = raw != null ? String(raw).trim() : "";
  return value;
}

/** cardInfo.platformRechargeRate — 平台充值服务费率（§二-4） */
export function resolveCardInfoPlatformRechargeRate(cardInfo) {
  const raw =
    cardInfo?.platformRechargeRate ??
    cardInfo?.platform_recharge_rate ??
    cardInfo?.rechargeFee ??
    cardInfo?.recharge_fee;
  return raw != null && raw !== "" ? raw : null;
}

/** cardInfo.hyperPayRechargeRate — HyperPay 充值费率（§二-4） */
export function resolveCardInfoHyperPayRechargeRate(cardInfo) {
  const raw =
    cardInfo?.hyperPayRechargeRate ??
    cardInfo?.hyper_pay_recharge_rate;
  return raw != null && raw !== "" ? raw : null;
}

function parseCardInfoRateValue(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Management Fee Top-Up Fee：platformRechargeRate + hyperPayRechargeRate */
export function resolveCardInfoTopUpFeeRate(cardInfo) {
  const platformRaw =
    cardInfo?.platformRechargeRate ??
    cardInfo?.platform_recharge_rate;
  const hyperPayRaw = resolveCardInfoHyperPayRechargeRate(cardInfo);
  if (platformRaw == null && hyperPayRaw == null) return null;

  const platformRate = platformRaw != null ? parseCardInfoRateValue(platformRaw) : 0;
  const hyperPayRate = hyperPayRaw != null ? parseCardInfoRateValue(hyperPayRaw) : 0;
  if (platformRaw != null && platformRate == null) return null;
  if (hyperPayRaw != null && hyperPayRate == null) return null;

  return platformRate + hyperPayRate;
}

/** 充值估算 Deposit fee rate：platformRate + hyperPayRechargeRate */
export function resolveRechargeEstimateDepositFeeRate(estimate) {
  if (!estimate || typeof estimate !== "object") return null;
  const platformRaw =
    estimate.platformRate ??
    estimate.platformRechargeRate ??
    estimate.platform_recharge_rate;
  const hyperPayRaw =
    estimate.hyperPayRechargeRate ??
    estimate.hyper_pay_recharge_rate;
  if (platformRaw == null && hyperPayRaw == null) return null;

  const platformRate = platformRaw != null ? parseCardInfoRateValue(platformRaw) : 0;
  const hyperPayRate = hyperPayRaw != null ? parseCardInfoRateValue(hyperPayRaw) : 0;
  if (platformRaw != null && platformRate == null) return null;
  if (hyperPayRaw != null && hyperPayRate == null) return null;

  return platformRate + hyperPayRate;
}

/** 最近交易列表默认查询区间（近 60 天，对齐 §五-16） */
export function buildRecentTransactionDateRange(maxSpanDays = 60) {
  return buildHyperpayTransactionDateRange({ maxSpanDays });
}

function extractHyperpayTransactionRecords(payload) {
  if (Array.isArray(payload?.records)) return payload.records;
  if (Array.isArray(payload?.result?.records)) return payload.result.records;
  if (Array.isArray(payload)) return payload;
  return null;
}

/** POST §五-16 按 business_type 查询并合并交易记录 */
export async function fetchMergedHyperpayTransactions({
  hyperPayCardId,
  startTime,
  endTime,
  page = 1,
  size = 20,
  businessTypes = HYPERPAY_TX_ALL_BUSINESS_TYPES,
}) {
  if (!hyperPayCardId) return [];

  const types =
    Array.isArray(businessTypes) && businessTypes.length
      ? businessTypes
      : HYPERPAY_TX_ALL_BUSINESS_TYPES;

  const responses = await Promise.all(
    types.map((businessType) =>
      post_hyperpayTransactions({
        card_id: hyperPayCardId,
        business_type: businessType,
        start_time: startTime,
        end_time: endTime,
        page,
        size,
      }).catch(() => null)
    )
  );

  const merged = [];
  const seen = new Set();
  for (let i = 0; i < responses.length; i += 1) {
    const records = extractHyperpayTransactionRecords(responses[i]);
    if (!Array.isArray(records)) continue;
    for (const record of records) {
      const normalized = normalizeTransactionRecord(record, types[i]);
      if (!normalized?.txId || seen.has(normalized.txId)) continue;
      seen.add(normalized.txId);
      merged.push(normalized);
    }
  }

  merged.sort((a, b) => Number(b.txTime) - Number(a.txTime));
  return merged;
}

/** 卡管理页 Recent transactions：查充值（business_type=2）前 N 条 */
export async function fetchRecentHyperpayTransactions(hyperPayCardId, limit = 5) {
  const range = buildRecentTransactionDateRange();
  const merged = await fetchMergedHyperpayTransactions({
    hyperPayCardId,
    startTime: range.start_time,
    endTime: range.end_time,
    page: 1,
    size: Math.max(limit, 10),
    businessTypes: [HYPERPAY_TX_BUSINESS_TYPES.RECHARGE],
  });
  return buildRecentTransactionsFromRecords(merged, limit);
}

export function mapRecentTransactionItem(record) {
  if (!record) return null;
  const title = String(record.title || record.description || "").trim();

  return {
    id: record.txId || record.id,
    title: title || undefined,
    spendAt: record.datetime || formatTransactionListDateTime(record.txTime),
    amountSigned: record.amountDisplay?.signed || "—",
    statusKey: record.statusKey || resolveTransactionStatusKey(record.txStatus),
  };
}

export function buildRecentTransactionsFromRecords(records, limit = 5) {
  if (!Array.isArray(records)) return [];
  return records
    .slice(0, limit)
    .map((item) => mapRecentTransactionItem(item))
    .filter(Boolean);
}

/** ACTIVE 态最近交易 mock（充值 business_type=2） */
export function buildMockRecentTransactions() {
  const rechargeOnly = buildMockTransactionList().filter(
    (item) => item.businessType === HYPERPAY_TX_BUSINESS_TYPES.RECHARGE
  );
  return buildRecentTransactionsFromRecords(rechargeOnly, 5);
}

/** 是否处于持卡管理阶段（ACTIVE / FROZEN） */
export function isCardManagementPhase(applyPhase) {
  return applyPhase === SPIKEX_CARD_APPLY_PHASES.ACTIVE || applyPhase === SPIKEX_CARD_APPLY_PHASES.FROZEN;
}

/** 余额币种前缀，默认 USD */
export function formatCardCurrencySymbol(cardCurrency) {
  const currency = String(cardCurrency || "usd").toLowerCase();
  if (currency === "usd") return "$";
  return `${currency.toUpperCase()} `;
}

/** 带币种的余额展示 */
export function formatCardBalanceWithCurrency(balance, cardCurrency) {
  const currency = String(cardCurrency || "usd").toLowerCase();
  const symbol = formatCardCurrencySymbol(cardCurrency);
  const currencyLabel = currency === "usd" ? "USD" : currency.toUpperCase();
  return `${symbol} ${formatCardBalanceDisplay(balance)} ${currencyLabel}`;
}

export function buildMockCardBalance(entryStatus) {
  return {
    availableBalance: entryStatus?.balance || "1334.345",
    currentBalance: entryStatus?.balance || "1334.345",
    cardCurrency: "usd",
    cardNumber: "111111******1234",
    cardType: "VIRTUAL",
    cardStatus: entryStatus?.status === "FROZEN" ? "FROZEN" : "ACTIVE",
  };
}

export function buildMockCardSensitive() {
  return {
    cardNumber: "4111111111111234",
    expire: "12/28",
    cvv: "888",
  };
}

export function normalizeVipProgress(raw) {
  if (!raw || typeof raw !== "object") return null;
  if (raw.result && typeof raw.result === "object") return raw.result;
  return raw;
}

/** 小数费率转百分比展示，如 0.015 → "1.5%" */
export function formatRatePercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const pct = n * 100;
  const text = pct % 1 === 0 ? String(Math.floor(pct)) : pct.toFixed(1).replace(/\.0$/, "");
  return `${text}%`;
}

function isFxFeeValuePresent(value) {
  if (value == null || value === "") return false;
  const n = Number(value);
  return Number.isFinite(n) && n !== 0;
}

/** 外汇费率区间；min/max 均为 0 或空时不展示（返回 null） */
export function formatFxFeeRange(min, max) {
  const hasMin = isFxFeeValuePresent(min);
  const hasMax = isFxFeeValuePresent(max);

  if (!hasMin && !hasMax) return null;

  if (hasMin && hasMax) {
    const minPct = formatRatePercent(min);
    const maxPct = formatRatePercent(max);
    if (minPct && maxPct) {
      return `${minPct.replace(/%$/, "")} - ${maxPct}`;
    }
  }

  if (hasMin) return formatRatePercent(min);
  if (hasMax) return formatRatePercent(max);
  return null;
}

/** 固定 USD 费用，如 0.5 → "0.5 USD" */
export function formatUsdFee(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const amount = n % 1 === 0 ? String(Math.floor(n)) : String(n);
  return `${amount} USD`;
}

/** 用卡规则弹窗费用展示，如 0.5 → "0.5U" */
export function formatCardRuleFee(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${formatUsdtAmount(value)}U`;
}

/** cardInfo 消费/验证相关单笔手续费 */
export function resolveCardInfoTransactionFees(cardInfo) {
  return {
    perTransaction:
      cardInfo?.perTransactionFee ??
      cardInfo?.per_transaction_fee,
    perFailed:
      cardInfo?.perFailedTransactionServiceFee ??
      cardInfo?.per_failed_transaction_service_fee,
    perAuth:
      cardInfo?.perTransactionAuthorizationFee ??
      cardInfo?.per_transaction_authorization_fee,
  };
}

/** cardInfo 限额金额，如 250000 → "250,000 USD" */
export function formatUsdCapAmount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return `${n.toLocaleString("en-US", { maximumFractionDigits: 0 })} USD`;
}

function resolveCardInfoCapField(cardInfo, field) {
  const snake = field.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
  const raw = cardInfo?.[field] ?? cardInfo?.[snake];
  return raw != null && raw !== "" ? raw : null;
}

/** cardInfo 月交易限额：`none` → i18n group.none，数值 → USD 限额 */
export function formatCardInfoMonthlyTransactionCap(cardInfo) {
  const raw = resolveCardInfoCapField(cardInfo, "monthlyTransactionCap");
  if (raw == null) return null;
  if (String(raw).trim().toLowerCase() === "none") {
    return { valueKey: "group.none" };
  }
  const value = formatUsdCapAmount(raw);
  return value ? { value } : null;
}

/** 规则弹窗：Card Features + Transaction Rule 数据 */
export function buildCardRulesModalSections(cardInfo) {
  const monthlyCap = formatCardInfoMonthlyTransactionCap(cardInfo);
  return {
    features: [
      "spikexCard.feeCardCurrency",
      "spikexCard.feeIssuedCountry",
      "spikexCard.feeAppleGooglePay",
      "spikexCard.feeOfflinePayment",
      "spikexCard.feeTransactionLimit",
    ],
    transactionCaps: [
      {
        key: "perTransaction",
        labelKey: "spikexCard.feePerTransactionCap",
        value: formatUsdCapAmount(cardInfo?.perTransactionCap ?? cardInfo?.per_transaction_cap) ?? "—",
      },
      {
        key: "daily",
        labelKey: "spikexCard.feeDailyCap",
        value:
          formatUsdCapAmount(cardInfo?.dailyTransactionCap ?? cardInfo?.daily_transaction_cap) ?? "—",
      },
      {
        key: "monthly",
        labelKey: "spikexCard.feeTransactionCap",
        ...(monthlyCap?.valueKey ? { valueKey: monthlyCap.valueKey } : { value: monthlyCap?.value ?? "—" }),
      },
      {
        key: "topUp",
        labelKey: "spikexCard.feeTopUpCap",
        value: formatUsdCapAmount(cardInfo?.perTopUpCap ?? cardInfo?.per_top_up_cap) ?? "—",
      },
    ],
  };
}

/** 开卡费展示，如 cardIssuedFee="5" → "$5"，"0" → "$0" */
export function formatCardIssuedFee(cardInfo) {
  if (!cardInfo || cardInfo.cardIssuedFee == null) return null;
  const n = Number(cardInfo.cardIssuedFee);
  if (!Number.isFinite(n)) return `$${cardInfo.cardIssuedFee}`;
  if (n === 0) return "$0";
  return `$${n % 1 === 0 ? Math.floor(n) : n}`;
}

export function clampReferralLevel(n) {
  const x = Number(n);
  if (!Number.isFinite(x) || x < 1) return 1;
  return Math.min(7, Math.floor(x));
}

/** URL 预览 mock 等级，如 /spikex-card?mockLevel=5 */
export function parseSpikexCardMockLevel(query) {
  const v = query?.mockLevel;
  if (v == null || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n < 1) return null;
  return clampReferralLevel(n);
}

/** URL 预览 mock 入口/申请状态 */
export const SPIKEX_CARD_MOCK_ENTRY_STATUSES = HYPERPAY_ENTRY_STATUSES;

export const SPIKEX_CARD_MOCK_APPLY_STATUSES = HYPERPAY_APPLY_STATUSES;

function normalizeMockQueryValue(value) {
  if (value == null || value === "") return null;
  return String(value).trim().toUpperCase();
}

/**
 * Mock URL 约定（与线上一致的两层状态）：
 * - mockLevel：VIP 等级（低于 mock requiredLevel 时锁定；mock 默认 requiredLevel 见 SPIKEX_CARD_UNLOCK_LEVEL）
 * - status：GET entry/status（7 种聚合态）
 * - applyStatus：POST apply/status（9 种明细，主要配合 status=REVIEWING）
 *
 * 示例：
 * /spikex-card?mockLevel=6&status=NO_CARD
 * /spikex-card?mockLevel=6&status=REVIEWING&applyStatus=FREEZING
 * /spikex-card?mockLevel=6&status=REVIEWING&applyStatus=APPROVED
 * /spikex-card?mockLevel=6&status=WAIT_ATTACHMENT
 * /spikex-card?mockLevel=6&status=REJECTED&applyStatus=ACTIVATE_REJECTED
 * /spikex-card?mockLevel=6&status=ACTIVE
 * /spikex-card?mockLevel=6&status=FROZEN
 *
 * 充值页 mock：
 * /spikex-card/recharge?mockRecharge=success&receivedAmount=500.00
 * /spikex-card/recharge?mockRecharge=form
 * /spikex-card?mockLevel=6&status=ACTIVE → 点 Deposit 进入充值（带 mock 卡 ID）
 */
export function mapApplyStatusToEntryStatus(applyStatus) {
  if (applyStatus === "WAIT_ATTACHMENT") return "WAIT_ATTACHMENT";
  if (applyStatus === "REJECTED" || applyStatus === "ACTIVATE_REJECTED" || applyStatus === "CANCELLED") {
    return "REJECTED";
  }
  if (applyStatus === "ACTIVE") return "ACTIVE";
  return "REVIEWING";
}

/** @deprecated 使用 mapApplyStatusToEntryStatus */
export function mapApplySubStatusToEntryStatus(applyStatus) {
  return mapApplyStatusToEntryStatus(applyStatus);
}

/** 解析 mock entry/status（query.status） */
export function parseSpikexCardMockEntryStatus(query) {
  const status = normalizeMockQueryValue(query?.status);
  const applyStatus = normalizeMockQueryValue(query?.applyStatus);

  if (status && HYPERPAY_ENTRY_STATUSES.has(status)) {
    return status;
  }

  // 兼容旧链接：status 传的是 applyStatus
  if (status && HYPERPAY_APPLY_STATUSES.has(status)) {
    return mapApplyStatusToEntryStatus(status);
  }

  // 仅传 applyStatus 时推导 entry
  if (!status && applyStatus && HYPERPAY_APPLY_STATUSES.has(applyStatus)) {
    return mapApplyStatusToEntryStatus(applyStatus);
  }

  return null;
}

/** 解析 mock apply/status（query.applyStatus） */
export function parseSpikexCardMockApplyStatus(query) {
  const applyStatus = normalizeMockQueryValue(query?.applyStatus);
  if (applyStatus && HYPERPAY_APPLY_STATUSES.has(applyStatus)) {
    return applyStatus;
  }

  const status = normalizeMockQueryValue(query?.status);
  // 兼容旧链接：apply 明细写在 status 上
  if (status && HYPERPAY_APPLY_STATUSES.has(status) && !HYPERPAY_ENTRY_STATUSES.has(status)) {
    return status;
  }

  return null;
}

/** @deprecated 使用 parseSpikexCardMockApplyStatus */
export function parseSpikexCardMockApplySubStatus(query) {
  return parseSpikexCardMockApplyStatus(query);
}

/** 构建 entry/status mock */
export function buildMockEntryStatus(entryStatus, options = {}) {
  const { applySubStatus, rejectReason } = options;
  const balance = entryStatus === "ACTIVE" ? "1334.345" : null;

  return {
    status: entryStatus,
    balance,
    cardId: entryStatus === "ACTIVE" || entryStatus === "FROZEN" ? 100001 : null,
    hyperPayCardId: entryStatus === "ACTIVE" || entryStatus === "FROZEN" ? "HP_CARD_MOCK_001" : null,
    applyId: ["REVIEWING", "WAIT_ATTACHMENT", "REJECTED"].includes(entryStatus) ? 378937912 : null,
    rejectReason:
      rejectReason ||
      (entryStatus === "REJECTED" ? "Document photo is unclear" : null),
    _mockApplySubStatus: applySubStatus || null,
  };
}

/** 构建 apply/status mock（补传 mcTradeNo、子状态细化） */
export function buildMockApplyStatus(applyStatus, entryStatus) {
  const failReasonByStatus = {
    REJECTED: "Document photo is unclear",
    ACTIVATE_REJECTED: "Activation verification failed",
    CANCELLED: "Application was cancelled",
    WAIT_ATTACHMENT: "Please upload proof of address to continue review",
  };

  const status =
    applyStatus ||
    (entryStatus === "REVIEWING"
      ? "REVIEWING"
      : entryStatus === "WAIT_ATTACHMENT"
        ? "WAIT_ATTACHMENT"
        : entryStatus === "REJECTED"
          ? "REJECTED"
          : null);
  if (!status) return null;

  return {
    applyId: 378937912,
    applyStatus: status,
    mcTradeNo: "HP_MOCK_001234567890",
    cardId: status === "ACTIVE" || entryStatus === "ACTIVE" ? 100001 : null,
    failReason: failReasonByStatus[status] || null,
  };
}

/** 构建 eligibility mock */
export function buildMockEligibility(level) {
  const currentLevel = clampReferralLevel(level);
  const eligible = currentLevel >= SPIKEX_CARD_UNLOCK_LEVEL;

  return {
    eligible,
    currentLevel,
    requiredLevel: SPIKEX_CARD_UNLOCK_LEVEL,
    issueFee: eligible ? "0" : "5",
    issueFeeWaived: eligible,
  };
}

/** 构建 VIP 进度 mock 数据（锁定页升级进度展示） */
export function buildMockVipProgress(level) {
  const currentLevel = clampReferralLevel(level);
  const isMaxLevel = currentLevel >= 7;
  const unlocked = resolveSpikexCardApplyEligibility(buildMockEligibility(currentLevel));

  return {
    currentVipLevel: currentLevel,
    nextVipLevel: isMaxLevel ? currentLevel : currentLevel + 1,
    isMaxLevel,
    currentDirectValidUserCount: 7,
    nextLevelRequiredDirectValidUserCount: 10,
    currentOpenPositionMargin: 130000,
    nextLevelRequiredOpenPositionMargin: 200000,
    ...(unlocked ? { currentVipLevelEffectiveTime: "2025-06-01T00:00:00.000Z" } : {}),
  };
}

export function resolveCurrentVipLevel(vip, eligibility) {
  if (eligibility?.currentLevel != null) {
    const lv = Number(eligibility.currentLevel);
    if (Number.isFinite(lv)) return lv <= 0 ? 1 : clampReferralLevel(lv);
  }
  if (vip?.currentVipLevel != null) {
    const lv = Number(vip.currentVipLevel);
    if (Number.isFinite(lv)) {
      return lv <= 0 ? 1 : clampReferralLevel(lv);
    }
  }
  return 1;
}

export function resolveNextVipLevel(vip, currentLevel) {
  if (vip?.isMaxLevel) return currentLevel;
  const n = Number(vip?.nextVipLevel);
  if (Number.isFinite(n) && n > 0) return clampReferralLevel(n);
  return Math.min(7, currentLevel + 1);
}

export function computeInviteVipTotalProgressPercent(vip) {
  if (!vip || typeof vip !== "object") return 0;
  if (vip.isMaxLevel === true) return 100;

  const reqU = Number(vip.nextLevelRequiredDirectValidUserCount);
  const reqM = Number(vip.nextLevelRequiredOpenPositionMargin);
  const curU = Number(vip.currentDirectValidUserCount);
  const curM = Number(vip.currentOpenPositionMargin);

  const r1 = reqU > 0 ? Math.min(1, Math.max(0, curU / reqU)) : 1;
  const r2 = reqM > 0 ? Math.min(1, Math.max(0, curM / reqM)) : 1;

  return Math.min(100, 50 * r1 + 50 * r2);
}

export function computeRemainOpenPositionMargin(vip) {
  if (!vip || typeof vip !== "object") return 0;
  if (vip.isMaxLevel === true) return 0;

  const reqM = Number(vip.nextLevelRequiredOpenPositionMargin);
  const curM = Number(vip.currentOpenPositionMargin);
  if (!Number.isFinite(reqM) || reqM <= 0) return 0;
  return Math.max(0, reqM - (Number.isFinite(curM) ? curM : 0));
}

export function computeRemainDirectValidUsers(vip) {
  if (!vip || typeof vip !== "object") return 0;
  if (vip.isMaxLevel === true) return 0;

  const reqU = Number(vip.nextLevelRequiredDirectValidUserCount);
  const curU = Number(vip.currentDirectValidUserCount);
  if (!Number.isFinite(reqU) || reqU <= 0) return 0;
  return Math.max(0, reqU - (Number.isFinite(curU) ? curU : 0));
}

export function hasNextLevelTradeRequirement(vip) {
  if (!vip || vip.isMaxLevel === true) return false;
  const reqM = Number(vip.nextLevelRequiredOpenPositionMargin);
  return Number.isFinite(reqM) && reqM > 0;
}

export function hasNextLevelInviteRequirement(vip) {
  if (!vip || vip.isMaxLevel === true) return false;
  const reqU = Number(vip.nextLevelRequiredDirectValidUserCount);
  return Number.isFinite(reqU) && reqU > 0;
}

export function formatUserCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return Math.max(0, Math.floor(n)).toLocaleString("en-US");
}

export function formatUsdAmount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString("en-US", { maximumFractionDigits: 0 });
}

export function parseVipEffectiveDate(effectiveTime) {
  if (!effectiveTime) return null;
  const d = new Date(effectiveTime);
  if (Number.isNaN(d.getTime())) return null;
  return {
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    day: d.getDate(),
  };
}

export function buildLevelDisplay(vipProgress, pageStatus, eligibility) {
  const unlockLevel = resolveSpikexCardDisplayUnlockLevel(eligibility);
  const currentLevel = resolveCurrentVipLevel(vipProgress, eligibility);
  const nextVipLevel = resolveNextVipLevel(vipProgress, currentLevel);

  if (pageStatus === 1) {
    const progressTarget =
      currentLevel < unlockLevel ? Math.min(nextVipLevel, unlockLevel) : nextVipLevel;
    return {
      unlockLevel,
      currentLevel,
      prevLevel: Math.max(1, currentLevel - 1),
      nextLevel: progressTarget,
      progressPercent: computeInviteVipTotalProgressPercent(vipProgress),
      tradeRemainUsd: formatUsdAmount(computeRemainOpenPositionMargin(vipProgress)),
      inviteRemainUsers: formatUserCount(computeRemainDirectValidUsers(vipProgress)),
      showTradeProgress: hasNextLevelTradeRequirement(vipProgress),
      showInviteProgress: hasNextLevelInviteRequirement(vipProgress),
    };
  }

  const unlockedDate = parseVipEffectiveDate(vipProgress?.currentVipLevelEffectiveTime);

  return {
    unlockLevel,
    currentLevel,
    prevLevel: Math.max(1, currentLevel - 1),
    nextLevel: vipProgress?.isMaxLevel ? currentLevel : nextVipLevel,
    unlockYear: unlockedDate?.year,
    unlockMonth: unlockedDate?.month,
    unlockDay: unlockedDate?.day,
  };
}

/** HyperPay 2.1.32：`first_name` / `last_name` 仅英文字母和空格，最多 50。紧急联系人前端沿用同一规则。 */
export const HYPERPAY_APPLY_NAME_MAX_LEN = 50;
export const HYPERPAY_APPLY_NAME_REGEXP = /^[a-zA-Z\s]{1,50}$/;

export function isValidHyperpayApplyName(value) {
  const text = String(value ?? "");
  return Boolean(text.trim()) && HYPERPAY_APPLY_NAME_REGEXP.test(text);
}

const SPIKEX_CARD_APPLY_TRIM_FIELDS = [
  ["firstName", "firstName（名）"],
  ["lastName", "lastName（姓）"],
  ["email", "email（邮箱）"],
  ["mobile", "mobile（手机号）"],
  ["mobileCode", "mobileCode（区号）"],
  ["docNo", "docNo（证件号）"],
  ["emergencyContact", "emergencyContact（紧急联系人）"],
  ["address", "address（地址）"],
  ["city", "city（城市）"],
  ["state", "state（州/省）"],
  ["zipCode", "zipCode（邮编）"],
];

export function trimApplyFormFields(source) {
  const trimmed = {};
  const undefinedFields = [];
  const nullFields = [];

  for (const [key, label] of SPIKEX_CARD_APPLY_TRIM_FIELDS) {
    const value = source?.[key];
    if (value === undefined) {
      undefinedFields.push(label);
      continue;
    }
    if (value === null) {
      nullFields.push(label);
      continue;
    }
    trimmed[key] = String(value).trim();
  }

  if (undefinedFields.length || nullFields.length) {
    const parts = [];
    if (undefinedFields.length) parts.push(`undefined: ${undefinedFields.join("、")}`);
    if (nullFields.length) parts.push(`null: ${nullFields.join("、")}`);
    throw new Error(`表单字段异常（${parts.join("；")}）`);
  }

  return trimmed;
}

export function formatUsdtAmount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  if (n % 1 === 0) return String(Math.floor(n));
  return String(n).replace(/\.?0+$/, "");
}

export function resolveCardIssuedFeeAmount(cardInfo, eligibility) {
  if (eligibility?.issueFeeWaived === true) return 0;
  const fromEligibility = Number(eligibility?.issueFee);
  if (Number.isFinite(fromEligibility)) return Math.max(0, fromEligibility);
  const n = Number(cardInfo?.cardIssuedFee ?? cardInfo?.card_issued_fee ?? 0);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

/** 当前是否免开卡费（实际应付为 0） */
export function isSpikexCardIssuedFeeWaived(cardInfo, eligibility) {
  return resolveCardIssuedFeeAmount(cardInfo, eligibility) <= 0;
}

export function resolveFirstRechargeAmount(cardInfo) {
  const n = Number(cardInfo?.firstRechargeAmount ?? cardInfo?.first_recharge_amount ?? 0);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

export function resolveApplyFreezeAmounts(cardInfo, eligibility) {
  const firstRecharge = resolveFirstRechargeAmount(cardInfo);
  const issuedFee = resolveCardIssuedFeeAmount(cardInfo, eligibility);
  const hasIssuedFee = issuedFee > 0;

  return {
    hasIssuedFee,
    firstRecharge,
    issuedFee,
    freezeTotal: hasIssuedFee ? firstRecharge + issuedFee : firstRecharge,
  };
}

/** §二-3 precheck.required：需冻结支付币总额（BRL 时为雷亚尔），无接口值时本地兜底 */
export function resolveApplyPrecheckRequired(precheck, cardInfo, eligibility, payCurrency) {
  const currency = resolveApplyPrecheckPayCurrency(
    precheck,
    payCurrency ?? DEFAULT_HYPERPAY_PAY_CURRENCY
  );
  const fromApi = Number(precheck?.required);
  if (Number.isFinite(fromApi) && fromApi >= 0) {
    return { amount: fromApi, payCurrency: currency };
  }
  return {
    amount: resolveApplyFreezeAmounts(cardInfo, eligibility).freezeTotal,
    payCurrency: DEFAULT_HYPERPAY_PAY_CURRENCY,
  };
}

/** §二-3 precheck.required：需冻结总额（兼容旧调用，返回数值） */
export function resolveApplyPrecheckRequiredUsdt(precheck, cardInfo, eligibility, payCurrency) {
  return resolveApplyPrecheckRequired(precheck, cardInfo, eligibility, payCurrency).amount;
}

export function formatApplyFreezeAmountLabel({
  precheck,
  cardInfo,
  eligibility,
  payCurrency,
  unit,
} = {}) {
  const currency = resolveApplyPrecheckPayCurrency(precheck, payCurrency);
  const { amount } = resolveApplyPrecheckRequired(precheck, cardInfo, eligibility, currency);
  if (!Number.isFinite(amount) || amount <= 0) return "--";
  if (unit === "U" || unit === "u") {
    return formatPayCurrencyAmountLabel(amount, currency);
  }
  return formatPayCurrencyDisplay(amount, currency);
}

/** 开卡冻结金额明细（开卡费 + 首充 estimate/crypto 估算支付） */
export function buildApplyFreezeBreakdown({
  precheck,
  cardInfo,
  eligibility,
  rechargeEstimate,
  payCurrency,
} = {}) {
  const currency = resolveApplyPrecheckPayCurrency(precheck, payCurrency);
  const { amount: freezeTotal } = resolveApplyPrecheckRequired(
    precheck,
    cardInfo,
    eligibility,
    currency
  );
  const { hasIssuedFee, firstRecharge, issuedFee } = resolveApplyFreezeAmounts(cardInfo, eligibility);
  const estimate = rechargeEstimate && typeof rechargeEstimate === "object" ? rechargeEstimate : null;

  const estimatedPayUsdt = Number(estimate?.totalCoinAmount);
  const hasEstimate = Number.isFinite(estimatedPayUsdt) && estimatedPayUsdt > 0;
  const firstRechargePayUsdt = hasEstimate ? estimatedPayUsdt : Math.max(0, freezeTotal - issuedFee);
  const firstRechargePayAmount = Number(
    estimate?.payAmount ?? estimate?.pay_amount ?? firstRechargePayUsdt
  );
  const exchangeRate = resolveHyperpayRechargeExchangeRate(estimate);
  const convertFeeAmount = resolveHyperpayRechargeConvertFeeAmount(estimate);
  const usdtRequired = pickEstimateAmount(precheck, "usdtRequired", "usdt_required");

  const pickAmount = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };

  return {
    payCurrency: currency,
    freezeTotal,
    firstRechargeUsd: firstRecharge,
    issuedFee,
    hasIssuedFee,
    firstRechargePayUsdt,
    firstRechargePayAmount: Number.isFinite(firstRechargePayAmount)
      ? firstRechargePayAmount
      : firstRechargePayUsdt,
    rechargeFeeUsdt: resolveHyperpayRechargeFeeAmount(estimate),
    platformFeeUsdt: pickAmount(estimate?.platformFeeAmount),
    platformRate:
      estimate?.platformRate ?? estimate?.platformRechargeRate ?? cardInfo?.platformRechargeRate,
    hyperPayRechargeRate:
      estimate?.hyperPayRechargeRate ??
      estimate?.hyper_pay_recharge_rate ??
      cardInfo?.hyperPayRechargeRate ??
      cardInfo?.hyper_pay_recharge_rate,
    realRechargeUsd: pickAmount(estimate?.realRechargeAmount) ?? firstRecharge,
    hasEstimate,
    exchangeRate,
    convertFeeAmount,
    usdtRequired,
    available: pickAmount(precheck?.available),
    sufficient: precheck?.sufficient,
  };
}

export function buildApplyPrecheckInsufficientTipValues(cardInfo, precheck, eligibility, payCurrency) {
  const currency = resolveApplyPrecheckPayCurrency(precheck, payCurrency);
  const amounts = resolveApplyFreezeAmounts(cardInfo, eligibility);
  const { amount: freezeTotal } = resolveApplyPrecheckRequired(
    precheck,
    cardInfo,
    eligibility,
    currency
  );
  return {
    hasIssuedFee: amounts.hasIssuedFee,
    freezeTotal: formatUsdtAmount(freezeTotal),
    firstRecharge: formatUsdtAmount(amounts.firstRecharge),
    issuedFee: formatUsdtAmount(amounts.issuedFee),
    available: formatUsdtAmount(precheck?.available),
    currency,
    isMultiCurrency: false,
  };
}

/** 并行拉取各支付币种的 apply/precheck */
export async function fetchApplyPrecheckForCurrencies(getPrecheck, cardInfo) {
  const currencies = resolveHyperpayPayCurrencies(cardInfo);
  return Promise.all(
    currencies.map(async (payCurrency) => {
      try {
        const precheck = await getPrecheck({ payCurrency, isAutoPopError: false });
        return {
          payCurrency,
          precheck: precheck && typeof precheck === "object" ? precheck : null,
        };
      } catch {
        return { payCurrency, precheck: null };
      }
    })
  );
}

/** 任一币种 sufficient === true 即可继续开卡 */
export function resolveCanProceedWithApplyPrechecks(precheckEntries) {
  if (!Array.isArray(precheckEntries) || !precheckEntries.length) return true;
  const resolved = precheckEntries.filter((item) => item?.precheck != null);
  if (!resolved.length) return true;
  return resolved.some((item) => item.precheck.sufficient === true);
}

/** 余额不足弹窗文案：单币种沿用原逻辑，双币种均不足时汇总展示 */
export function buildApplyPrecheckInsufficientTipValuesMulti(
  cardInfo,
  precheckEntries,
  eligibility
) {
  const entries = Array.isArray(precheckEntries)
    ? precheckEntries.filter((item) => item?.precheck?.sufficient === false)
    : [];
  if (entries.length <= 1) {
    const single = entries[0] ?? precheckEntries?.[0];
    return buildApplyPrecheckInsufficientTipValues(
      cardInfo,
      single?.precheck,
      eligibility,
      single?.payCurrency
    );
  }

  const amounts = resolveApplyFreezeAmounts(cardInfo, eligibility);
  const lines = entries.map(({ payCurrency, precheck }) => {
    const { amount } = resolveApplyPrecheckRequired(
      precheck,
      cardInfo,
      eligibility,
      payCurrency
    );
    return {
      currency: payCurrency,
      freezeTotal: formatUsdtAmount(amount),
      available: formatUsdtAmount(precheck?.available),
    };
  });
  const usdtLine = lines.find((line) => line.currency === HYPERPAY_PAY_CURRENCIES.USDT);
  const brlLine = lines.find((line) => line.currency === HYPERPAY_PAY_CURRENCIES.BRL);

  return {
    hasIssuedFee: amounts.hasIssuedFee,
    isMultiCurrency: true,
    lines,
    usdtRequired: usdtLine?.freezeTotal ?? "0",
    usdtAvailable: usdtLine?.available ?? "0",
    brlRequired: brlLine?.freezeTotal ?? "0",
    brlAvailable: brlLine?.available ?? "0",
  };
}

/** 根据 mockLevel 推导默认 entry status */
export function resolveMockEntryStatusFromLevel(mockLevel, mockEntryStatus, mockApplyStatus) {
  if (mockEntryStatus) {
    return buildMockEntryStatus(mockEntryStatus, { applySubStatus: mockApplyStatus });
  }
  if (mockLevel == null) return null;
  if (!resolveSpikexCardApplyEligibility(buildMockEligibility(mockLevel))) {
    return buildMockEntryStatus("LEVEL_LOCKED");
  }
  return buildMockEntryStatus("NO_CARD");
}
